import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'
import { CourseLmsProfileType } from 'enum'
import CourseForm from 'components/ui/forms/course/CourseForm'
import { CourseFormPageTitle } from 'components/ui/forms/course/components/CourseFormPageTitle'
import { NewCourse } from 'models/course.model'
import * as fromActions from 'store/actions/courses.action'
import { OnSubmitValues } from 'components/ui/forms/course/types'
import { useAccess } from 'contexts/AccessContext'
import { IMainState } from 'store/states'
import Wrapper from 'components/reusables/wrapper'
import { getSynchronousCourse } from '../utils'

export const New: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const initialNormative =
    attestationsEnabled &&
    new URLSearchParams(location.search).get('normative') === '1'
  const { can, access, loading } = useAccess()
  const canManageCourses = can('manageInternalCourses')
  const canAssignProfiles = access.canAssignCourseProfiles()

  useEffect(() => {
    if (loading) return
    if (!canManageCourses) window.location.href = '/'
  }, [loading, canManageCourses])

  const handleSubmit = (values: OnSubmitValues, onlySave: boolean) => {
    let coursesLmsProfilesAttributes: any[] = []
    if (values.requiredProfiles)
      coursesLmsProfilesAttributes = values.requiredProfiles.map(
        ({ value }: any) => ({
          lmsProfileId: value,
          kind: CourseLmsProfileType.Required,
        })
      )
    if (values.optionalProfiles)
      coursesLmsProfilesAttributes = [
        ...coursesLmsProfilesAttributes,
        ...values.optionalProfiles.map(({ value }: any) => ({
          lmsProfileId: value,
          kind: CourseLmsProfileType.Optional,
        })),
      ]

    const synchronousCourse = getSynchronousCourse(values)

    const course = NewCourse({
      name: values.name,
      description: values.description,
      courseType: values.courseType,
      state: values.state?.value,
      startDate: values.startDate ? String(values.startDate) : '',
      untilDate: values.untilDate ? String(values.untilDate) : '',
      banner: values.banner,
      thumbnail: values.thumbnail,
      ...(canAssignProfiles
        ? { coursesLmsProfilesAttributes: coursesLmsProfilesAttributes }
        : {}),
      invitationNotification: values.invitationNotification,
      canGenerateCertificate: values.canGenerateCertificate,
      reminderNotification: values.reminderNotification,
      reminderNoStartCourseNotification:
        values.reminderNoStartCourseNotification,
      enrolCreatedNotification: values.enrolCreatedNotification,
      synchronousCourse: synchronousCourse,
      hours: values.hours,
      canRestart: values.canRestart ?? false,
      integrations: values.integrations,
      normative: values.normative,
    })

    dispatch({
      type: fromActions.CREATE_COURSE,
      payload: { course, onlySave, history },
    })
  }

  const handleCancel = () => history.push('/administration/courses/')

  return (
    <Wrapper shadow={true}>
      <CourseFormPageTitle
        title={t('administration.courses.new.pageTitle')}
        onBack={handleCancel}
      />
      <CourseForm onSubmit={handleSubmit} initialNormative={initialNormative} />
    </Wrapper>
  )
}

export default New
